// NodeDateModal.jsx

import React, { useEffect, useRef, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './NodeDateModal.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import NodeDateInput from 'node/form/fields/date/NodeDateInput';

/**
 * NodeDateModal
 *
 * Modal for editing schema-driven date fields.
 *
 * @param {object} value - Current field value.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {Function} setOpen - Setter for modal open state.
 * @param {Function} onChange - Callback when new value is saved.
 * 
 * @returns {ReactNode} NodeDateModal component.
 */
const NodeDateModal = ({ value, isOpen, setOpen, onChange }) => {

  // ----- STATE ----- //

  const [date, setDate] = useState(null);

  // ----- REFS ----- //

  // Ref to hold the most up-to-date date value for saving on blur or save click, since 
  // the local date state may be stale due to React's async state updates.

  const dateRef = useRef(null);

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- EFFECTS ----- //

  // Clone local state on open

  useEffect(() => {
    if (isOpen) {
      const nextValue = value ? { ...value } : null;
      dateRef.current = nextValue;
      setDate(nextValue);
    }
  }, [isOpen, value]);

  // ----- HANDLERS ----- //

  /**
 * Handles date change.
 *
 * @param {*} nextValue
 */

  const handleDateChange = (nextValue) => {
    dateRef.current = nextValue;
    setDate(nextValue);
  };

  /**
   * Handles save by blurring active element to trigger any onBlur saves in the date input, then
   * calling onChange with the most up-to-date date value from the ref and closing the modal.
   */
  const handleSave = () => {
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (typeof onChange === 'function') {
      onChange(dateRef.current ?? date);
    }
    setOpen(false);
  };

  // ----- RENDER ----- //

  return (
    <Modal
      title="Date"
      isOpen={isOpen}
      width={isMobile ? '100%' : 380}
      height="auto"
      onClose={() => setOpen(false)}
    >
      <ModalForm>

        {/* WRAPPER */}
        <div className="node-date-modal-wrapper">
          <NodeDateInput
            value={date}
            hybrid={false}
            onChange={handleDateChange}
          />
        </div>
      </ModalForm>

      <ModalButtons>
        <Button label="Save" onClick={handleSave} />
      </ModalButtons>

    </Modal>
  );
};

// Dev Tools
NodeDateModal.displayName = 'NodeDateModal';

export default NodeDateModal;
